import { z } from 'zod';

/**
 * The Property Setup module's contracts.
 *
 * One schema for the form, one for the API read and one for the gateway
 * vocabulary. Keeping them together keeps the form and the service in step.
 */

const MAX_URL = 512;

const optionalUrl = (message: string) => z.url({ message }).max(MAX_URL).or(z.literal(''));

export const propertySetupFormInput = z.object({
  propertyId: z.uuid(),
  facebookUrl: optionalUrl('Facebook URL must be a valid URL'),
  instagramUrl: optionalUrl('Instagram URL must be a valid URL'),
  twitterUrl: optionalUrl('Twitter URL must be a valid URL'),
  totalRooms: z.string().regex(/^\d+$/, 'Total rooms must be a whole number'),
  smtpGatewayId: z.uuid().or(z.literal('')),
  smsGatewayId: z.uuid().or(z.literal('')),
  paymentGatewayId: z.uuid().or(z.literal('')),
  selectedAmenities: z.record(z.string(), z.boolean()),
  amenityFree: z.record(z.string(), z.boolean()),
});

export type PropertySetupFormInput = z.infer<typeof propertySetupFormInput>;

export const setupPropertyRow = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
});

export type SetupPropertyRow = z.infer<typeof setupPropertyRow>;

export const notificationGateway = z.object({
  id: z.string(),
  name: z.string(),
});

export type NotificationGateway = z.infer<typeof notificationGateway>;

export const propertySetupGateways = z.object({
  email: z.array(notificationGateway),
  sms: z.array(notificationGateway),
});

export type PropertySetupGateways = z.infer<typeof propertySetupGateways>;

const propertyAmenityOption = z.object({
  amenityId: z.string(),
  name: z.string(),
  isFree: z.boolean(),
  isSelected: z.boolean(),
});

export const propertySetupConfiguration = z.object({
  facebookUrl: z.string().nullable(),
  instagramUrl: z.string().nullable(),
  twitterUrl: z.string().nullable(),
  totalRooms: z.number(),
  smtpGatewayId: z.string().nullable(),
  smsGatewayId: z.string().nullable(),
  paymentGatewayId: z.string().nullable(),
  version: z.number(),
});

export const propertySetupResponse = z.object({
  propertyId: z.string(),
  configuration: propertySetupConfiguration,
  amenities: z.array(propertyAmenityOption),
});

export type PropertySetupResponse = z.infer<typeof propertySetupResponse>;
